// ============================================================================
// DirectorGridBoard (F-617..623) — outil de planification salariale du directeur
// ============================================================================
// Port FIDELE du PoC components/salary/director-grid-board.tsx (874 L @ SHA
// 7e40e4a). La GRILLE est la surface de gestion unique : clic cellule pour
// deplacer dans la classe, menu « ... » par ligne, rangee « + Ajouter » par
// classe, icone historique pour saisir une position passee. Bandeau d'impact
// masse en tete de carte ; brouillon persiste + soumission au CE en pied.
//
// TRANSPOSITION (PLAN-SOUS-LOTS §SL-B, PLAN-ATTAQUE) — strictement :
//  - server actions Next → mutations TanStack (usePlanification, SL-A fige) ;
//  - `<form action>` → onClick + mutation (5 formulaires : draft/submit/backfill+/-) ;
//  - autosave `useEffect` debounce (decision C2 #2) : mecanique lastSavedPayloadRef
//    conservee, l'action devient une mutation silencieuse (F-613, pas d'invalidation) ;
//  - re-skin @gs/ui : Card/StatCard/Badge/Modal locaux (employee/ui) + tokens ;
//  - Dialog @base-ui → Modal (gabarit ManagementTab) ; Intl fr-CA en dur → helpers
//    locale-aware (format.ts, P14) ; aucune refonte d'etat (STOP-question non armee).
//
// M-654 (lock, PUR CLIENT) : `locked = pending !== null`. TOUS les handlers
// commencent par `if (locked) return`. Aucune garde serveur inventee — le PoC ne
// verrouille QUE client, l'UQ draft filtre protege la course (P4 tranche).
//
// M-655 (off-grid, A 4 ENDROITS) : (a) isMoved → false ; (b) handleCellClick →
// return ; (c) moveLineToClass → return ; (d) exclusion masse grille via
// payrollTotals (scenario.offGrid signale « comptes au cout reel »). Une ligne
// hors grille n'est deplacable sur AUCUN des 3 chemins (test M-655 exige).
//
// M-657 : le board ne recoit PAS de taux horaire individuel ; il consomme le
// roster nominatif scoped (legitime, director) + `realByEmail` (base reelle +
// extras) — la frise agregee vit ailleurs (PayrollCharts, SL-C). Regle #4 : zero
// emoji. Regle #28 : tokens dark-aware, zero CSS en dur cote couleur.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GS_COMPONENTS } from '@gs/ui';
import { Check, ChevronDown, CircleAlert, Loader2, RotateCcw, Save, Send, Trash2 } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { Card, CardHeader, Badge, StatCard, Modal } from '../employee/ui';
import {
  buildEmployeeLines,
  type EmployeeGridLine,
  type ScenarioChangeInput,
} from '../../lib/salary/positions';
import {
  payrollTotals,
  positionAnnual,
  realCostTotals,
  scenarioDelta,
  type MemberRealCost,
  type RealScenarioMember,
  type ScenarioMember,
} from '../../lib/salary/scenario';
import type {
  EchelonChange,
  EchelonChangeReason,
  GridPosition,
  SalaryClass,
} from '../../lib/types/firestore';
import {
  useAutosaveScenario,
  useSaveScenarioDraft,
  useSubmitScenario,
  useSavePastPosition,
  useDeletePastPosition,
  planificationErrorCode,
  type PlanningGridMember,
  type PlanningGridPosition,
  type PlanningEchelonChange,
  type PlanningScenario,
  type SaveScenarioPayload,
} from '../../hooks/usePlanification';
import { EmployeeGrid } from './EmployeeGrid';
import { formatCad, formatSignedCad, formatSignedPct, formatDate, formatTime } from './format';

// Membre positionne dans la grille — miroir du DTO (M-657 : pas de taux horaire).
export type GridMember = PlanningGridMember;

type Position = { class_id: string | null; echelon: number };

// ─── Adaptateurs DTO → types lib (buildEmployeeLines) ──────────────────────────
// Le DTO gest strippe des champs que le PoC (payload serveur Next) portait :
// GridPosition sans created_at/updated_at ; EchelonChange sans id/email/
// evaluation_id/changed_by_email (M-657/timestamps). buildEmployeeLines ne LIT que
// class_id/echelon/source/year (positions) et effective_year/from_echelon/
// to_echelon (historique) — les champs manquants sont des shims de type inertes.
// Meme categorie que `adaptGridClasses` (SL-A) : le pont vit cote client, le
// serveur est fige. Aucune donnee inventee n'est affichee.

function toLibPast(rec: Record<string, PlanningGridPosition[]>): Record<string, GridPosition[]> {
  const out: Record<string, GridPosition[]> = {};
  for (const [email, arr] of Object.entries(rec)) {
    out[email] = arr.map((p) => ({
      email: p.email,
      year: p.year,
      class_id: p.class_id,
      echelon: p.echelon,
      source: p.source,
      note: p.note,
      entered_by_email: p.entered_by_email,
      created_at: '',
      updated_at: '',
    }));
  }
  return out;
}

function toLibHistory(
  rec: Record<string, PlanningEchelonChange[]>,
): Record<string, EchelonChange[]> {
  const out: Record<string, EchelonChange[]> = {};
  for (const [email, arr] of Object.entries(rec)) {
    out[email] = arr
      // Sans annee d'effet : aucune ligne passee positionnable — on l'ecarte.
      .filter((c) => c.effective_year !== null)
      .map((c) => ({
        id: '',
        email,
        from_echelon: c.from_echelon,
        to_echelon: c.to_echelon,
        effective_year: c.effective_year as number,
        reason: c.reason as EchelonChangeReason,
        evaluation_id: null,
        changed_by_email: '',
        created_at: c.created_at,
      }));
  }
  return out;
}

// ─── Bandeau de statut (remplace `Alert` du PoC) ───────────────────────────────

function ScenarioAlert({
  tone,
  title,
  children,
}: {
  tone: 'info' | 'success' | 'warning';
  title: string;
  children?: ReactNode;
}) {
  const toneCls =
    tone === 'success'
      ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
      : tone === 'warning'
        ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
        : 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
  return (
    <div role="status" className={`rounded-xl border-l-4 p-4 text-field ${toneCls}`}>
      <p className="font-semibold">{title}</p>
      {children ? <div className="mt-1 text-meta">{children}</div> : null}
    </div>
  );
}

// ─── Disclosure (remplace `Collapsible` du PoC) ────────────────────────────────

function Disclosure({ summary, children }: { summary: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full touch-manipulation items-center justify-between gap-2 px-3 py-2 text-field font-medium text-slate-700 dark:text-slate-200"
      >
        <span>{summary}</span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? <div className="border-t border-slate-200 px-3 py-2 dark:border-slate-700">{children}</div> : null}
    </div>
  );
}

const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';

/**
 * Outil de planification salariale du directeur. Voir l'entete du fichier pour le
 * contrat M-654/M-655/M-657 et la transposition. `classes` arrive deja adapte en
 * `SalaryClass` (adaptGridClasses, SL-A) ; `members`/scenarios/positions/historique
 * arrivent en snake_case (miroir lib) — seuls past/historique passent par un shim
 * de type (toLibPast/toLibHistory) avant buildEmployeeLines.
 */
export function DirectorGridBoard({
  classes,
  members,
  currentYear,
  gridYear,
  teamId,
  draft,
  pending,
  lastDecided,
  pastByEmail,
  historyByEmail,
  gridYears,
  realByEmail,
  specialHoursByEmail,
}: {
  classes: SalaryClass[];
  members: GridMember[];
  currentYear: number;
  // Pivot de projection des taux (annee des taux de `classes`). gridYear ===
  // currentYear ⇒ aucune projection.
  gridYear: number;
  teamId: string;
  draft: PlanningScenario | null;
  pending: PlanningScenario | null;
  lastDecided: PlanningScenario | null;
  pastByEmail: Record<string, PlanningGridPosition[]>;
  historyByEmail: Record<string, PlanningEchelonChange[]>;
  gridYears: number[];
  realByEmail: Record<string, MemberRealCost>;
  specialHoursByEmail: Record<string, number>;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [searchParams, setSearchParams] = useSearchParams();

  const saveDraft = useSaveScenarioDraft();
  const submitScenario = useSubmitScenario();
  const { mutateAsync: autosaveAsync } = useAutosaveScenario();

  const locked = pending !== null;

  // ?ok=/?error= en SPA (P21) : deep-link observable, pose apres mutation. La page
  // (PlanificationPage) rend la banniere — ici on ne fait que poser les params.
  function emitResult(ok?: string, error?: string) {
    const next = new URLSearchParams(searchParams);
    next.delete('ok');
    next.delete('error');
    if (ok) next.set('ok', ok);
    if (error) next.set('error', error);
    setSearchParams(next, { replace: true });
  }

  // Positions du scenario, par email — initialisees depuis le brouillon persiste.
  const [positions, setPositions] = useState<Record<string, Position>>(() => {
    const init: Record<string, Position> = {};
    for (const c of draft?.changes ?? []) {
      if (!c.virtual && c.email) {
        init[c.email] = { class_id: c.to_class_id, echelon: c.to_echelon };
      }
    }
    return init;
  });
  const [virtuals, setVirtuals] = useState<ScenarioMember[]>(() =>
    (draft?.changes ?? [])
      .filter((c) => c.virtual && c.to_class_id)
      .map((c, i) => ({
        id: `virtuel-${i + 1}`,
        name: c.name,
        class_id: c.to_class_id,
        echelon: c.to_echelon,
        virtual: true,
      })),
  );
  const [virtualSeq, setVirtualSeq] = useState(
    (draft?.changes ?? []).filter((c) => c.virtual).length + 1,
  );
  const [note, setNote] = useState(draft?.note ?? '');
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  // ── Sauvegarde auto du brouillon (protection contre la perte de travail) ───
  type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(draft?.updated_at ?? null);
  const [autosavedId, setAutosavedId] = useState<string | null>(null);
  const lastSavedPayloadRef = useRef<string | null>(null);

  // Dialog de saisie d'une position passee.
  const [backfillFor, setBackfillFor] = useState<GridMember | null>(null);

  const positionOf = (m: GridMember): Position =>
    positions[m.email] ?? { class_id: m.class_id, echelon: m.echelon };

  const isMoved = (m: GridMember): boolean => {
    if (m.offGrid) return false; // M-655 (a) : hors grille — jamais deplacable
    const p = positionOf(m);
    return p.class_id !== m.class_id || p.echelon !== m.echelon;
  };

  const hasChanges = members.some(isMoved) || virtuals.length > 0;

  // Masse actuelle (positions reelles) vs scenario (positions modifiees + fictifs).
  const baselineMembers: ScenarioMember[] = members.map((m) => ({
    id: m.email,
    name: m.name,
    class_id: m.class_id,
    echelon: m.echelon,
    virtual: false,
    offGrid: m.offGrid,
  }));
  const scenarioMembers: ScenarioMember[] = [
    ...members.map((m) => {
      const p = positionOf(m);
      return {
        id: m.email,
        name: m.name,
        class_id: p.class_id,
        echelon: p.echelon,
        virtual: false,
        offGrid: m.offGrid,
      };
    }),
    ...virtuals,
  ];

  const baseline = payrollTotals(baselineMembers, classes, currentYear, gridYear);
  const scenario = payrollTotals(scenarioMembers, classes, currentYear, gridYear);
  const delta = scenarioDelta(baseline.total, scenario.total);

  // Cout REEL (impact budget) : part du cout reel actuel de chaque employe.
  const realMembers: RealScenarioMember[] = [
    ...members.map((m) => ({
      id: m.email,
      before: { class_id: m.class_id, echelon: m.echelon },
      after: positionOf(m),
      virtual: false,
      offGrid: m.offGrid,
    })),
    ...virtuals.map((v) => ({
      id: v.id,
      before: null,
      after: { class_id: v.class_id, echelon: v.echelon },
      virtual: true,
    })),
  ];
  const real = realCostTotals(realMembers, realByEmail, classes, currentYear, gridYear);

  // Detail avant → apres des membres modifies + fictifs (detail repliable).
  const changes = [
    ...members.filter(isMoved).map((m) => {
      const p = positionOf(m);
      return {
        id: m.email,
        name: m.name,
        before: positionAnnual(m.class_id, m.echelon, classes, currentYear, gridYear),
        after: positionAnnual(p.class_id, p.echelon, classes, currentYear, gridYear),
        virtual: false,
      };
    }),
    ...virtuals.map((v) => ({
      id: v.id,
      name: v.name,
      before: null,
      after: positionAnnual(v.class_id, v.echelon, classes, currentYear, gridYear),
      virtual: true,
    })),
  ];

  // Changements au format persiste (payload du brouillon + grille).
  const scenarioChanges: ScenarioChangeInput[] = [
    ...members.filter(isMoved).map((m) => {
      const p = positionOf(m);
      return {
        email: m.email,
        name: m.name,
        virtual: false,
        from_class_id: m.class_id,
        from_echelon: m.echelon,
        to_class_id: p.class_id,
        to_echelon: p.echelon,
      };
    }),
    ...virtuals.map((v) => ({
      local_id: v.id,
      email: null,
      name: v.name,
      virtual: true,
      from_class_id: null,
      from_echelon: null,
      to_class_id: v.class_id,
      to_echelon: v.echelon,
    })),
  ];

  // Quand un scenario est soumis, la grille reflete SES changements (figes).
  const displayedChanges: ScenarioChangeInput[] = locked
    ? (pending?.changes ?? []).map((c, i) => ({
        ...c,
        local_id: c.virtual ? `soumis-${i}` : undefined,
      }))
    : scenarioChanges;

  // Recalcule a chaque render — ~30 lignes, negligeable.
  const linesByClass = buildEmployeeLines({
    members,
    pastPositions: toLibPast(pastByEmail),
    echelonHistory: toLibHistory(historyByEmail),
    scenarioChanges: displayedChanges,
    currentYear,
  });

  // Payload persiste (compare pour l'autosave). L'objet sert a la mutation.
  const savePayloadObj: SaveScenarioPayload = {
    team_id: teamId,
    year: currentYear,
    note: note.trim() || null,
    changes: scenarioChanges.map(({ local_id: _l, ...c }) => c),
  };
  const savePayload = JSON.stringify(savePayloadObj);

  // Autosave en debounce (~1,5 s) : ne declenche que sur un vrai changement, et
  // jamais quand la grille est verrouillee. Le premier passage ne fait
  // qu'enregistrer la baseline (etat initial = deja persiste).
  useEffect(() => {
    if (lastSavedPayloadRef.current === null) {
      lastSavedPayloadRef.current = savePayload;
      return;
    }
    if (locked || !hasChanges) return;
    if (savePayload === lastSavedPayloadRef.current) return;

    setSaveStatus('pending');
    const payloadAtSave = savePayload;
    const timer = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const res = await autosaveAsync(JSON.parse(payloadAtSave) as SaveScenarioPayload);
        if (res.ok) {
          lastSavedPayloadRef.current = payloadAtSave;
          setLastSavedAt(res.savedAt);
          if (res.scenarioId) setAutosavedId(res.scenarioId);
          // N'affiche « enregistre » que si rien de neuf n'est arrive entre-temps.
          setSaveStatus((s) => (s === 'saving' ? 'saved' : s));
        } else {
          setSaveStatus('error');
        }
      } catch {
        setSaveStatus('error');
      }
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savePayload, hasChanges, locked, autosaveAsync]);

  // Filet de securite : avertit avant fermeture/rechargement tant qu'une
  // sauvegarde est en attente ou en vol.
  const hasUnsaved = saveStatus === 'pending' || saveStatus === 'saving';
  useEffect(() => {
    if (!hasUnsaved) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  // ── Handlers grille (tous no-op si verrouille) ─────────────────────────────

  // Clic sur une cellule : pose/deplace le marqueur scenario. Re-clic sur la
  // cellule scenario, ou clic sur la position reelle = retire.
  function handleCellClick(line: EmployeeGridLine, classId: string, echelon: number) {
    if (locked) return;
    if (line.virtual) {
      setVirtuals((prev) =>
        prev.map((v) => (v.id === line.id ? { ...v, class_id: classId, echelon } : v)),
      );
      return;
    }
    const m = members.find((x) => x.email === line.email);
    if (!m) return;
    if (m.offGrid) return; // M-655 (b) : hors grille — non deplacable
    const p = positionOf(m);
    const isSameAsScenario = p.class_id === classId && p.echelon === echelon;
    const isReal = m.class_id === classId && m.echelon === echelon;
    if (isSameAsScenario || isReal) {
      // Retire le marqueur scenario (retour a la position reelle).
      setPositions((prev) => {
        const { [m.email]: _omit, ...rest } = prev;
        return rest;
      });
      if (isReal) return;
    }
    if (!isSameAsScenario) {
      setPositions((prev) => ({ ...prev, [m.email]: { class_id: classId, echelon } }));
    }
  }

  // Rangee « + Ajouter un employe » : cree un fictif a la position cliquee.
  function addVirtualAt(classId: string, echelon: number, name: string) {
    if (locked) return;
    setVirtuals((prev) => [
      ...prev,
      {
        id: `virtuel-${virtualSeq}`,
        name: name.trim() || t('gest:planification.board.virtualDefaultName', { n: virtualSeq }),
        class_id: classId,
        echelon,
        virtual: true,
      },
    ]);
    setVirtualSeq((n) => n + 1);
  }

  // Menu « ... » « Deplacer vers <classe> » : pose le scenario a l'echelon 1 de
  // la cible — la ligne se materialise dans la classe cible, ajustable par clic.
  function moveLineToClass(line: EmployeeGridLine, targetClassId: string) {
    if (locked) return;
    if (line.virtual) {
      setVirtuals((prev) =>
        prev.map((v) => (v.id === line.id ? { ...v, class_id: targetClassId, echelon: 1 } : v)),
      );
      return;
    }
    const m = members.find((x) => x.email === line.email);
    if (!m) return;
    if (m.offGrid) return; // M-655 (c) : hors grille — non deplacable
    const p = positionOf(m);
    if (p.class_id === targetClassId) return;
    setPositions((prev) => ({
      ...prev,
      [m.email]: { class_id: targetClassId, echelon: 1 },
    }));
  }

  function removeScenario(line: EmployeeGridLine) {
    if (locked) return;
    if (line.virtual) {
      setVirtuals((prev) => prev.filter((v) => v.id !== line.id));
      return;
    }
    if (!line.email) return;
    setPositions((prev) => {
      const { [line.email as string]: _omit, ...rest } = prev;
      return rest;
    });
  }

  function renameVirtual(lineId: string, name: string) {
    if (locked) return;
    setVirtuals((prev) => prev.map((v) => (v.id === lineId ? { ...v, name } : v)));
  }

  function reset() {
    setPositions({});
    setVirtuals([]);
    setConfirmSubmit(false);
  }

  // ── Mutations (server actions → TanStack) ──────────────────────────────────

  function onSaveDraft() {
    saveDraft.mutate(savePayloadObj, {
      onSuccess: (res) => {
        setAutosavedId(res.scenarioId);
        lastSavedPayloadRef.current = savePayload;
        setSaveStatus('idle');
        emitResult('draft_saved');
      },
      onError: (err) => emitResult(undefined, planificationErrorCode(err)),
    });
  }

  function onSubmit() {
    const id = draft?.id ?? autosavedId;
    if (!id) return;
    submitScenario.mutate(id, {
      onSuccess: () => emitResult('submitted'),
      onError: (err) => emitResult(undefined, planificationErrorCode(err)),
    });
  }

  // Annees saisissables pour le passe : annees de grille passees, sinon repli.
  const pastYears = (() => {
    const fromGrid = gridYears.filter((y) => y < currentYear);
    if (fromGrid.length > 0) return fromGrid.sort((a, b) => b - a);
    return Array.from({ length: 8 }, (_, i) => currentYear - 1 - i);
  })();

  const statusBadge = locked ? (
    <Badge tone="info">{t('gest:planification.board.status.submittedOn', { date: formatDate(pending?.submitted_at ?? null, locale) })}</Badge>
  ) : draft ? (
    <Badge tone="neutral">{t('gest:planification.board.status.draftOn', { date: formatDate(draft.updated_at, locale) })}</Badge>
  ) : (
    <Badge tone="neutral">{t('gest:planification.board.status.none')}</Badge>
  );

  const canSubmit = Boolean((draft?.id ?? autosavedId) && scenarioChanges.length > 0);
  const submitBusy = hasUnsaved || submitScenario.isPending;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex flex-col gap-4">
          {/* ── Impact masse salariale (tuiles) ──────────────────────────────── */}
          <CardHeader title={t('gest:planification.board.impactTitle')} action={statusBadge} />

          <p className={labelCls}>{t('gest:planification.board.gridGroup')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label={t('gest:planification.board.currentMass')}
              value={formatCad(delta.baseline, locale)}
              hint={t('gest:planification.board.currentMassHint')}
            />
            <StatCard
              label={t('gest:planification.board.scenarioMass')}
              value={formatCad(delta.scenario, locale)}
              hint={hasChanges ? t('gest:planification.board.withChanges') : t('gest:planification.board.noChange')}
            />
            <StatCard
              label={t('gest:planification.board.annualDelta')}
              value={formatSignedCad(delta.deltaAnnual, locale)}
              tone={delta.deltaAnnual !== 0 ? 'brand' : undefined}
            />
            <StatCard
              label={t('gest:planification.board.pctDelta')}
              value={formatSignedPct(delta.deltaPct, locale)}
            />
          </div>

          {/* ── Cout reel (impact budget) ────────────────────────────────────── */}
          <p className={labelCls}>{t('gest:planification.board.realGroup')}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label={t('gest:planification.board.realCurrent')}
              value={formatCad(real.baseline, locale)}
              hint={t('gest:planification.board.realCurrentHint')}
            />
            <StatCard
              label={t('gest:planification.board.realScenario')}
              value={formatCad(real.scenario, locale)}
              hint={hasChanges ? t('gest:planification.board.realScenarioHint') : t('gest:planification.board.noChange')}
            />
            <StatCard
              label={t('gest:planification.board.realDelta')}
              value={formatSignedCad(real.deltaAnnual, locale)}
              tone={real.deltaAnnual !== 0 ? 'brand' : undefined}
            />
            <StatCard
              label={t('gest:planification.board.employerCharge')}
              value={formatCad(real.charge, locale)}
              hint={t('gest:planification.board.employerChargeHint')}
            />
          </div>
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:planification.board.impactExplainer')}
          </p>

          {/* ── Statut du scenario ───────────────────────────────────────────── */}
          {pending ? (
            <ScenarioAlert tone="info" title={t('gest:planification.board.submittedTitle')}>
              {t('gest:planification.board.submittedBody', { date: formatDate(pending.submitted_at, locale) })}
            </ScenarioAlert>
          ) : lastDecided ? (
            <ScenarioAlert
              tone={lastDecided.status === 'approved' ? 'success' : 'warning'}
              title={
                lastDecided.status === 'approved'
                  ? t('gest:planification.board.approvedTitle', { date: formatDate(lastDecided.decided_at, locale) })
                  : t('gest:planification.board.rejectedTitle', { date: formatDate(lastDecided.decided_at, locale) })
              }
            >
              {lastDecided.decision_note ? `« ${lastDecided.decision_note} »` : null}
            </ScenarioAlert>
          ) : null}

          {/* ── Grille : la surface de gestion unique (STUB en SL-B) ─────────── */}
          <EmployeeGrid
            classes={classes}
            linesByClass={linesByClass}
            currentYear={currentYear}
            interactive={!locked}
            onCellClick={handleCellClick}
            onBackfillRequest={(line) => {
              const m = members.find((x) => x.email === line.email);
              if (m) setBackfillFor(m);
            }}
            onAddVirtual={locked ? undefined : addVirtualAt}
            onMoveToClass={locked ? undefined : moveLineToClass}
            onRemoveScenario={locked ? undefined : removeScenario}
            onRenameVirtual={locked ? undefined : renameVirtual}
            specialHoursByEmail={specialHoursByEmail}
          />
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('gest:planification.board.gridHelp')}
          </p>

          {/* ── Detail avant → apres, repliable (sous la grille) ─────────────── */}
          {changes.length > 0 ? (
            <Disclosure summary={t('gest:planification.board.changesDetail', { n: changes.length })}>
              <ul className="flex flex-col">
                {changes.map((c) => {
                  const d = c.after !== null && c.before !== null ? c.after - c.before : null;
                  return (
                    <li
                      key={c.id}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/60 py-2 text-field last:border-b-0 dark:border-slate-700/60"
                    >
                      <span className="flex items-center gap-2 font-medium text-slate-800 dark:text-slate-100">
                        {c.name}
                        {c.virtual ? <Badge tone="info">{t('gest:planification.board.virtualBadge')}</Badge> : null}
                      </span>
                      <span className="tabular-nums text-slate-500 dark:text-slate-400">
                        {c.before !== null ? formatCad(c.before, locale) : '—'} →{' '}
                        {c.after !== null ? formatCad(c.after, locale) : '—'}
                        {d !== null ? (
                          <span className={d >= 0 ? 'ml-2 text-brand-600 dark:text-brand-400' : 'ml-2 text-green-700 dark:text-green-500'}>
                            ({formatSignedCad(d, locale)})
                          </span>
                        ) : c.virtual && c.after !== null ? (
                          <span className="ml-2 text-brand-600 dark:text-brand-400">
                            ({formatSignedCad(c.after, locale)})
                          </span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {scenario.unpriced.length > 0 ? (
                <p className="pt-2 text-meta text-slate-500 dark:text-slate-400">
                  {t('gest:planification.board.unpriced', { n: scenario.unpriced.length })}
                </p>
              ) : null}
              {scenario.offGrid.length > 0 ? (
                <p className="pt-2 text-meta text-slate-500 dark:text-slate-400">
                  {t('gest:planification.board.offGridExcluded', { n: scenario.offGrid.length })}
                </p>
              ) : null}
            </Disclosure>
          ) : null}

          {/* ── Barre d'actions : brouillon / soumission ─────────────────────── */}
          {locked ? null : (
            <div className="flex flex-col gap-3 border-t border-slate-200/60 pt-4 dark:border-slate-700/60">
              {/* Statut de la sauvegarde auto — protege contre la perte de travail. */}
              {saveStatus === 'saving' || saveStatus === 'pending' ? (
                <p className="flex items-center gap-1.5 text-meta text-slate-500 dark:text-slate-400">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                  {t('gest:planification.board.autosaving')}
                </p>
              ) : saveStatus === 'saved' ? (
                <p className="flex items-center gap-1.5 text-meta text-green-700 dark:text-green-500">
                  <Check className="size-3.5" aria-hidden="true" />
                  {t('gest:planification.board.autosaved', { time: formatTime(lastSavedAt, locale) })}
                </p>
              ) : saveStatus === 'error' ? (
                <p className="flex items-center gap-1.5 text-meta text-red-600 dark:text-red-400">
                  <CircleAlert className="size-3.5" aria-hidden="true" />
                  {t('gest:planification.board.autosaveError')}
                </p>
              ) : null}
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex min-w-52 flex-1 flex-col gap-1.5">
                  <label htmlFor="scenario-note" className={labelCls}>
                    {t('gest:planification.board.noteLabel')}
                  </label>
                  <input
                    id="scenario-note"
                    className={GS_COMPONENTS.input}
                    placeholder={t('gest:planification.board.notePlaceholder')}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={onSaveDraft}
                  disabled={(!hasChanges && !draft) || saveDraft.isPending}
                  className={`${GS_COMPONENTS.btnSecondary} touch-manipulation inline-flex items-center gap-1.5`}
                >
                  <Save size={16} aria-hidden="true" />
                  {t('gest:planification.board.saveDraft')}
                </button>
                {canSubmit ? (
                  confirmSubmit ? (
                    <button
                      type="button"
                      onClick={onSubmit}
                      disabled={submitBusy}
                      className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}
                    >
                      <Send size={16} aria-hidden="true" />
                      {t('gest:planification.board.confirmSubmit')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmSubmit(true)}
                      className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}
                    >
                      <Send size={16} aria-hidden="true" />
                      {t('gest:planification.board.submit')}
                    </button>
                  )
                ) : null}
                {hasChanges ? (
                  <button
                    type="button"
                    onClick={reset}
                    className={`${GS_COMPONENTS.btnGhost} touch-manipulation inline-flex items-center gap-1.5`}
                  >
                    <RotateCcw size={16} aria-hidden="true" />
                    {t('gest:planification.board.reset')}
                  </button>
                ) : null}
              </div>
              {confirmSubmit ? (
                <p className="text-meta text-slate-500 dark:text-slate-400">
                  {t('gest:planification.board.confirmHelp')}
                </p>
              ) : !hasChanges && !draft ? (
                <p className="text-meta text-slate-500 dark:text-slate-400">
                  {t('gest:planification.board.emptyHelp')}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </Card>

      {/* ── Dialog : saisir une position passee ────────────────────────────── */}
      <Modal
        open={backfillFor !== null}
        onClose={() => setBackfillFor(null)}
        title={backfillFor ? t('gest:planification.backfill.title', { name: backfillFor.name }) : ''}
      >
        <p className="text-field text-slate-500 dark:text-slate-400">
          {t('gest:planification.backfill.help')}
        </p>
        {backfillFor ? (
          <BackfillForm
            member={backfillFor}
            classes={classes}
            teamId={teamId}
            pastYears={pastYears}
            existing={(pastByEmail[backfillFor.email] ?? []).filter((p) => p.source === 'manual')}
            onResult={emitResult}
            onClose={() => setBackfillFor(null)}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function BackfillForm({
  member,
  classes,
  teamId,
  pastYears,
  existing,
  onResult,
  onClose,
}: {
  member: GridMember;
  classes: SalaryClass[];
  teamId: string;
  pastYears: number[];
  existing: PlanningGridPosition[];
  onResult: (ok?: string, error?: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const savePosition = useSavePastPosition();
  const deletePosition = useDeletePastPosition();

  const [year, setYear] = useState(String(pastYears[0] ?? ''));
  const [classId, setClassId] = useState(member.class_id ?? classes[0]?.id ?? '');
  const [echelon, setEchelon] = useState('1');
  const [note, setNote] = useState('');

  const cls = classes.find((c) => c.id === classId);
  const classLabel = (id: string) => classes.find((c) => c.id === id)?.label ?? id;

  function onSave() {
    if (!year || !classId) return;
    savePosition.mutate(
      {
        email: member.email,
        year: Number(year),
        class_id: classId,
        echelon: Number(echelon),
        note: note.trim() || null,
        team_id: teamId,
      },
      {
        onSuccess: () => {
          onResult('position_saved');
          onClose();
        },
        onError: (err) => onResult(undefined, planificationErrorCode(err)),
      },
    );
  }

  function onDelete(y: number) {
    deletePosition.mutate(
      { email: member.email, year: y, team_id: teamId },
      {
        onSuccess: () => onResult('position_deleted'),
        onError: (err) => onResult(undefined, planificationErrorCode(err)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="backfill-year" className={labelCls}>
              {t('gest:planification.backfill.year')}
            </label>
            <select
              id="backfill-year"
              className={`${GS_COMPONENTS.input} w-28`}
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {pastYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="backfill-class" className={labelCls}>
              {t('gest:planification.backfill.class')}
            </label>
            <select
              id="backfill-class"
              className={`${GS_COMPONENTS.input} w-44`}
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
            >
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="backfill-echelon" className={labelCls}>
              {t('gest:planification.backfill.echelon')}
            </label>
            <select
              id="backfill-echelon"
              className={`${GS_COMPONENTS.input} w-28`}
              value={echelon}
              onChange={(e) => setEchelon(e.target.value)}
            >
              {(cls?.echelons ?? []).map((e) => (
                <option key={e.echelon} value={e.echelon}>
                  {t('gest:planification.backfill.echelonOption', { n: e.echelon })}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="backfill-note" className={labelCls}>
            {t('gest:planification.backfill.note')}
          </label>
          <input
            id="backfill-note"
            className={GS_COMPONENTS.input}
            placeholder={t('gest:planification.backfill.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={savePosition.isPending || !year || !classId}
          className={`${GS_COMPONENTS.btnAction} inline-flex w-fit items-center gap-1.5`}
        >
          <Save size={16} aria-hidden="true" />
          {t('gest:planification.backfill.save')}
        </button>
      </form>

      {existing.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-chip font-semibold uppercase tracking-wide text-slate-400">
            {t('gest:planification.backfill.savedPositions')}
          </p>
          <ul className="flex flex-col gap-1">
            {existing.map((p) => (
              <li
                key={p.year}
                className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-field dark:border-slate-700"
              >
                <span className="tabular-nums text-slate-700 dark:text-slate-200">
                  {t('gest:planification.backfill.positionLine', {
                    year: p.year,
                    class: classLabel(p.class_id),
                    echelon: p.echelon,
                  })}
                </span>
                <button
                  type="button"
                  onClick={() => onDelete(p.year)}
                  disabled={deletePosition.isPending}
                  aria-label={t('gest:planification.backfill.deleteAria', { year: p.year })}
                  className={`${GS_COMPONENTS.btnGhost} touch-manipulation inline-flex items-center p-1.5`}
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
